import { useRef, useState } from "react"
import { makeStyles } from "tss-react/mui"
import { ButtonBase, Popover, Typography } from "@mui/material"
import { faChevronDown } from "@fortawesome/pro-solid-svg-icons"
import { faCalendarDay } from "@fortawesome/pro-regular-svg-icons"
import { StaticDatePicker } from "@mui/x-date-pickers"
import type { DateTime } from "luxon"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"

import { useDateTime } from "../../../../../../modules/localization/useDateTime"

const useStyles = makeStyles()((theme) => ({
    dateButton: {
        display: "flex",
        alignItems: "center",
        gap: ".5rem",
        padding: ".75rem 1rem",
        borderRadius: ".75rem",
        border: `1px solid ${theme.palette.tailwind.slate[300]}`,
        background: theme.palette.common.white,
        color: theme.palette.tailwind.slate[500],
        fontWeight: 400,
        fontSize: "1rem",
        flexShrink: 0,
        transition: "background 0.15s, border-color 0.15s",
        "&:hover": {
            borderColor: theme.palette.tailwind.slate[400],
        },
    },
    dateButtonSelected: {
        background: theme.palette.tailwind.slate[800],
        borderColor: "transparent",
        color: theme.palette.common.white,
        "&:hover": {
            background: theme.palette.tailwind.slate[700],
            borderColor: "transparent",
        },
    },
    dateButtonIcon: {
        fontSize: "1.125rem",
        flexShrink: 0,
        color: theme.palette.tailwind.slate[500],
    },
    dateButtonIconSelected: {
        color: theme.palette.common.white,
    },
    dateButtonChevron: {
        fontSize: ".75rem",
        flexShrink: 0,
        opacity: 0.6,
    },
}))

type Props = Readonly<{
    selectedDate: DateTime
    isCustomDate: boolean
    onDateChange: (date: DateTime) => void
}>

export function DatePickerButton({ selectedDate, isCustomDate, onDateChange }: Props) {
    const { classes, cx } = useStyles()
    const dt = useDateTime()
    const anchorRef = useRef<HTMLButtonElement>(null)
    const [pickerOpen, setPickerOpen] = useState(false)

    return (
        <>
            <ButtonBase
                ref={anchorRef}
                className={cx(classes.dateButton, isCustomDate && classes.dateButtonSelected)}
                onClick={() => setPickerOpen(true)}
            >
                <FontAwesomeIcon
                    icon={faCalendarDay}
                    className={cx(classes.dateButtonIcon, isCustomDate && classes.dateButtonIconSelected)}
                />
                <Typography variant="body1" component="span">
                    {dt.formatDate(selectedDate)}
                </Typography>
                <FontAwesomeIcon icon={faChevronDown} className={classes.dateButtonChevron} />
            </ButtonBase>

            <Popover
                open={pickerOpen}
                anchorEl={anchorRef.current}
                onClose={() => setPickerOpen(false)}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
            >
                <StaticDatePicker
                    value={selectedDate}
                    onChange={(date) => {
                        if (date) {
                            onDateChange(date)
                            setPickerOpen(false)
                        }
                    }}
                    disablePast
                    onAccept={() => setPickerOpen(false)}
                    slotProps={{ actionBar: { actions: [] } }}
                />
            </Popover>
        </>
    )
}
